import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AutoAttendCancelModal,
  AutoAttendMeetingCard,
  Button,
  EmptyState,
  PageHeader,
  Tabs,
} from '@copilot/ui';
import Icon from '../components/common/Icon';
import Loader from '../components/common/Loader';
import RescheduleAutoAttendModal from '../components/autoAttend/RescheduleAutoAttendModal';
import ScheduleAutoAttendForm from '../components/autoAttend/ScheduleAutoAttendForm';
import {
  ACTIVE_STATES,
  BOT_ERROR_CODES,
  BOT_POLL_INTERVAL_MS,
} from '../constants/botMeetings';
import { LANGUAGES } from '../constants/languages';
import { useAuth } from '../hooks/useAuth';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import * as botMeetingApi from '../services/botMeetingApi';
import '../styles/auto-attend.css';
import {
  UNAVAILABLE_MESSAGE,
  describeBotError,
  isUnavailableError,
  autoAttendDisplayName,
} from '../utils/botMeetings';

const TABS = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
];

function AutoAttendPage() {
  const { user } = useAuth();

  const [tab, setTab] = useState('upcoming');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [notice, setNotice] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [rescheduleTarget, setRescheduleTarget] = useState(null);

  // Only the newest request may write state: a slow response for a tab
  // the user already left, or an older poll, must never overwrite it.
  const requestId = useRef(0);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const load = useCallback(async (whichTab, { silent = false } = {}) => {
    const thisRequest = ++requestId.current;

    if (!silent) {
      setIsLoading(true);
      setError('');
    }

    try {
      const data = await botMeetingApi.listBotMeetings(whichTab);
      if (!isMounted.current || thisRequest !== requestId.current) return;

      setItems(Array.isArray(data) ? data : []);
      setUnavailable(false);
      setError('');
    } catch (err) {
      if (!isMounted.current || thisRequest !== requestId.current) return;

      if (isUnavailableError(err)) {
        setUnavailable(true);
        setItems([]);
      } else if (!silent) {
        setError(describeBotError(err, 'Unable to load Auto Attend meetings. Please try again.'));
      }
      // A failed background refresh keeps what is already on screen.
    } finally {
      if (isMounted.current && thisRequest === requestId.current && !silent) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    setItems([]);
    load(tab);
  }, [tab, load]);

  // Live status: poll only while an Upcoming bot is actually doing
  // something, never while the tab is hidden, and never twice at once.
  const hasActive =
    tab === 'upcoming' && items.some((m) => ACTIVE_STATES.includes(m.bot_state));

  useEffect(() => {
    if (!hasActive) return undefined;

    let timer = null;

    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const start = () => {
      if (timer === null && !document.hidden) {
        timer = setInterval(() => load('upcoming', { silent: true }), BOT_POLL_INTERVAL_MS);
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Catch up immediately after being hidden, then resume.
        load('upcoming', { silent: true });
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [hasActive, load]);

  // Idle (nothing active) screens pick up changes from other clients via
  // the generic silent refresh. While a bot is active the poll above owns
  // refreshing, so this stays off to avoid a second timer on the endpoint.
  const autoRefreshList = useCallback(() => load(tab, { silent: true }), [tab, load]);
  useAutoRefresh(autoRefreshList, { enabled: !hasActive && !isLoading });

  const refresh = () => load(tab, { silent: true });

  const handleScheduled = () => {
    setIsFormOpen(false);
    setNotice({ type: 'success', text: 'Auto Attend scheduled.' });
    if (tab === 'upcoming') {
      refresh();
    } else {
      setTab('upcoming');
    }
  };

  const handleRescheduled = () => {
    setRescheduleTarget(null);
    setNotice({ type: 'success', text: 'Auto Attend rescheduled.' });
    refresh();
  };

  const closeCancel = () => {
    setCancelTarget(null);
    setCancelError('');
  };

  const confirmCancel = async () => {
    if (!cancelTarget || isCancelling) return;

    const target = cancelTarget;
    setIsCancelling(true);
    setCancelError('');

    try {
      await botMeetingApi.cancelBotMeeting(target.meeting_id);
      if (!isMounted.current) return;

      setItems((current) => current.filter((m) => m.meeting_id !== target.meeting_id));
      closeCancel();
      setNotice({ type: 'success', text: 'Auto Attend cancelled.' });
      refresh();
    } catch (err) {
      if (!isMounted.current) return;

      const message = describeBotError(err, 'Unable to cancel Auto Attend. Please try again.');
      if (
        err?.code === BOT_ERROR_CODES.STATE_CONFLICT ||
        err?.code === BOT_ERROR_CODES.NOT_FOUND
      ) {
        // The bot's state changed underneath us: show the truth.
        closeCancel();
        setNotice({ type: 'error', text: message });
        refresh();
      } else {
        setCancelError(message);
      }
    } finally {
      if (isMounted.current) setIsCancelling(false);
    }
  };

  const openForm = () => {
    setNotice(null);
    setIsFormOpen(true);
  };

  const scheduleButton = !unavailable && (
    <Button onClick={openForm}>
      <Icon name="plus" />
      Schedule Auto Attend
    </Button>
  );

  const isUpcoming = tab === 'upcoming';

  return (
    <div className="auto-attend-page">
      <PageHeader
        title="Auto Attend"
        description="Can't attend a meeting? Let Auto Attend join for you. It joins at the scheduled time, records, and you get the transcript and summary afterwards."
        action={scheduleButton || undefined}
      />

      {unavailable && (
        <div className="form-alert" role="alert">
          {UNAVAILABLE_MESSAGE}
        </div>
      )}

      {notice && (
        <div
          className={`auto-attend-notice auto-attend-notice--${notice.type}`}
          role={notice.type === 'error' ? 'alert' : 'status'}
        >
          <span>{notice.text}</span>
          <button type="button" aria-label="Dismiss message" onClick={() => setNotice(null)}>
            <Icon name="x" size={15} />
          </button>
        </div>
      )}

      {!unavailable && (
        <>
          <Tabs tabs={TABS} activeKey={tab} onChange={setTab} />

          <section className="auto-attend-list-panel" aria-label={`${isUpcoming ? 'Upcoming' : 'Past'} Auto Attend meetings`}>
            {isLoading && <Loader label="Loading Auto Attend meetings..." />}

            {!isLoading && error && (
              <div className="form-alert" role="alert">
                {error}
              </div>
            )}

            {!isLoading && !error && items.length === 0 && (
              <EmptyState
                title={isUpcoming ? 'No upcoming Auto Attend meetings' : 'No past Auto Attend meetings'}
                description={
                  isUpcoming
                    ? "Schedule Auto Attend for a meeting you can't attend."
                    : 'Completed and cancelled Auto Attend meetings will appear here.'
                }
                action={
                  isUpcoming ? (
                    <Button onClick={openForm}>
                      <Icon name="plus" />
                      Schedule Auto Attend
                    </Button>
                  ) : undefined
                }
              />
            )}

            {!isLoading && !error && items.length > 0 && (
              <div className="auto-attend-list">
                {items.map((meeting) => (
                  <AutoAttendMeetingCard
                    key={meeting.meeting_id}
                    meeting={meeting}
                    variant={tab}
                    botName={autoAttendDisplayName(user)}
                    languages={LANGUAGES}
                    onCancel={(m) => {
                      setNotice(null);
                      setCancelTarget(m);
                    }}
                    onReschedule={(m) => {
                      setNotice(null);
                      setRescheduleTarget(m);
                    }}
                    resultsHref={(m) => `/app/meeting/${m.meeting_id}`}
                    renderLink={({ href, className, children }) => (
                      <Link to={href} className={className}>
                        {children}
                      </Link>
                    )}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {isFormOpen && (
        <ScheduleAutoAttendForm
          onClose={() => setIsFormOpen(false)}
          onScheduled={handleScheduled}
        />
      )}

      {cancelTarget && (
        <AutoAttendCancelModal
          onKeep={closeCancel}
          onConfirm={confirmCancel}
          isSubmitting={isCancelling}
          error={cancelError}
        />
      )}

      {rescheduleTarget && (
        <RescheduleAutoAttendModal
          meeting={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onRescheduled={handleRescheduled}
          onStale={(message) => {
            setRescheduleTarget(null);
            setNotice({ type: 'error', text: message });
            refresh();
          }}
        />
      )}
    </div>
  );
}

export default AutoAttendPage;
